import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Building2,
  Target,
  Users,
  Beaker,
  DollarSign,
  TrendingUp,
  Newspaper,
  BarChart3,
  Brain,
  CheckCircle2,
  XCircle,
  ShieldCheck,
  ExternalLink,
  Tag,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAccountCampaigns, type AccountCampaignSummary } from "@/hooks/useAccountCampaigns";

interface GoldSheetTabProps {
  company: Record<string, any>;
}

function useGoldSheetData(accountId: string) {
  const contacts = useQuery({
    queryKey: ["gold-sheet-contacts", accountId],
    queryFn: async () => {
      const { count: total } = await supabase
        .from("contacts")
        .select("*", { count: "exact", head: true })
        .eq("company_id", accountId);
      const { count: withEmail } = await supabase
        .from("contacts")
        .select("*", { count: "exact", head: true })
        .eq("company_id", accountId)
        .not("email", "is", null);
      return { total: total || 0, withEmail: withEmail || 0 };
    },
  });

  const opportunities = useQuery({
    queryKey: ["gold-sheet-opps", accountId],
    queryFn: async () => {
      const { data } = await supabase
        .from("pipeline_board" as any)
        .select("id, name, stage_name, stage_color, deal_value, single_sales_objective, next_action, next_action_date")
        .eq("account_id", accountId);
      return (data || []) as any[];
    },
  });

  const trials = useQuery({
    queryKey: ["gold-sheet-trials", accountId],
    queryFn: async () => {
      const { count } = await supabase
        .from("account_clinical_trials" as any)
        .select("*", { count: "exact", head: true })
        .eq("company_id", accountId);
      return count || 0;
    },
  });

  const grants = useQuery({
    queryKey: ["gold-sheet-grants", accountId],
    queryFn: async () => {
      const { count } = await supabase
        .from("account_grants" as any)
        .select("*", { count: "exact", head: true })
        .eq("company_id", accountId);
      return count || 0;
    },
  });

  const news = useQuery({
    queryKey: ["gold-sheet-news", accountId],
    queryFn: async () => {
      const { count } = await supabase
        .from("company_news" as any)
        .select("*", { count: "exact", head: true })
        .eq("company_id", accountId);
      return count || 0;
    },
  });

  const campaigns = useAccountCampaigns(accountId);

  return { contacts, opportunities, trials, grants, news, campaigns };
}

/**
 * Always-rendered two-column row. Missing values show as "—" so every account
 * has the same layout shape regardless of enrichment coverage.
 */
function InfoRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex justify-between py-1.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-right max-w-[60%] truncate">
        {value && value.toString().trim() !== "" ? value : "—"}
      </span>
    </div>
  );
}

function BoolRow({ label, value }: { label: string; value: boolean | null | undefined }) {
  return (
    <div className="flex justify-between py-1.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      {value === true ? (
        <span className="flex items-center gap-1 text-sm font-medium text-green-700">
          <CheckCircle2 className="h-3.5 w-3.5" />Yes
        </span>
      ) : value === false ? (
        <span className="flex items-center gap-1 text-sm font-medium text-muted-foreground">
          <XCircle className="h-3.5 w-3.5" />No
        </span>
      ) : (
        <span className="text-sm font-medium">—</span>
      )}
    </div>
  );
}

function excelSerialToYear(serial: string | number | null): string | null {
  if (serial == null) return null;
  const num = typeof serial === "string" ? parseInt(serial, 10) : serial;
  if (isNaN(num) || num < 1) return null;
  const date = new Date(Date.UTC(1899, 11, 30 + num));
  const year = date.getUTCFullYear();
  return year >= 1900 && year <= 2100 ? year.toString() : null;
}

function formatFoundedYear(foundingYear: number | null, carrFoundedDate: string | null): string | null {
  if (foundingYear) return foundingYear.toString();
  if (carrFoundedDate) return excelSerialToYear(carrFoundedDate);
  return null;
}

export function GoldSheetTab({ company }: GoldSheetTabProps) {
  const { contacts, opportunities, trials, grants, news, campaigns } = useGoldSheetData(company.id);

  const enrichment = company.enrichment_data || {};

  const fundingLabel: Record<string, string> = {
    seed: "Seed",
    pre_seed: "Pre-Seed",
    series_a: "Series A",
    series_b: "Series B",
    series_c: "Series C",
    series_d_plus: "Series D+",
    ipo: "IPO",
    public: "Public",
    private: "Private",
    acquired: "Acquired",
    bootstrapped: "Bootstrapped",
    non_profit: "Non-Profit",
    inactive: "Inactive",
  };

  const hq = [company.headquarters_city, company.headquarters_state].filter(Boolean).join(", ");
  const founded = formatFoundedYear(company.founding_year, company.carr_founded_date);
  const employees = company.employee_range || company.employee_count?.toString();

  return (
    <div className="p-6 space-y-5">
      {/* ── DOSSIER ── */}
      <Card>
        <CardContent className="p-0">
          {/* AI Summary / Classification (if exists) */}
          {(company.ai_summary || company.ai_subcategory || company.ai_classification_notes) && (
            <div className="px-5 py-3 border-b bg-violet-50/40">
              <div className="flex items-center gap-2 mb-1">
                <Brain className="h-3.5 w-3.5 text-violet-600 shrink-0" />
                <span className="text-[11px] font-semibold text-violet-700 uppercase tracking-wide">AI Positioning</span>
                {company.ai_subcategory && (
                  <Badge variant="secondary" className="text-[10px] ml-1">{company.ai_subcategory.replace(/_/g, " ")}</Badge>
                )}
                {enrichment.confidence && (
                  <span className="text-[10px] text-muted-foreground">({enrichment.confidence}% conf.)</span>
                )}
              </div>
              {company.ai_summary ? (
                <p className="text-sm text-foreground leading-relaxed">{company.ai_summary}</p>
              ) : company.ai_classification_notes ? (
                <p className="text-sm text-muted-foreground leading-relaxed">{company.ai_classification_notes}</p>
              ) : null}
            </div>
          )}

          {/* Description */}
          {company.description && (
            <div className="px-5 py-3 border-b">
              <p className="text-sm text-muted-foreground leading-relaxed line-clamp-4">{company.description}</p>
            </div>
          )}

          {/* ── COMPANY PROFILE ── */}
          <div className="px-5 py-4 border-b">
            <h3 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Building2 className="h-3.5 w-3.5" /> Company Profile
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-2">
              <InfoRow label="Category" value={company.primary_category?.replace(/_/g, " ")} />
              <InfoRow label="Subcategory" value={company.ai_subcategory?.replace(/_/g, " ")} />
              <InfoRow label="Dev Stage" value={company.development_stage} />
              <InfoRow label="Ownership" value={company.carr_ownership_type || company.ownership_type} />
              <InfoRow label="Founded" value={founded} />
              <InfoRow label="Employees" value={employees} />
              <InfoRow label="HQ" value={hq} />
              <InfoRow label="Territory" value={company.territory || company.region} />
              <InfoRow label="Funding" value={fundingLabel[company.funding_status] || company.funding_status} />
              <InfoRow
                label="Total Raised"
                value={company.carr_total_raised_usd ? `$${Number(company.carr_total_raised_usd).toLocaleString()}` : null}
              />
              <InfoRow label="Sales Rep" value={company.sales_rep} />
              <InfoRow label="Enrichment" value={company.enrichment_status?.replace(/_/g, " ")} />
            </div>
            {company.enrichment_notes && (
              <p className="text-xs text-muted-foreground mt-3">
                <span className="font-semibold">Enrichment notes:</span> {company.enrichment_notes}
              </p>
            )}
          </div>

          {/* ── SCIENCE & CARR FIT (always render; "—" where blank) ── */}
          <div className="px-5 py-4 border-b">
            <h3 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Beaker className="h-3.5 w-3.5" /> Science & CARR Fit
            </h3>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mb-3">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-xs text-muted-foreground shrink-0">Modalities:</span>
                {company.modalities?.length > 0 ? (
                  company.modalities.map((m: string) => (
                    <Badge key={m} variant="secondary" className="text-[10px]">{m}</Badge>
                  ))
                ) : (
                  <span className="text-sm">—</span>
                )}
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-xs text-muted-foreground shrink-0">Therapeutic:</span>
                {company.therapeutic_areas?.length > 0 ? (
                  company.therapeutic_areas.slice(0, 8).map((a: string) => (
                    <Badge key={a} variant="outline" className="text-[10px] bg-orange-500/10 text-orange-700 border-orange-500/20">{a}</Badge>
                  ))
                ) : (
                  <span className="text-sm">—</span>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-2">
              <InfoRow label="Clinical Phase" value={company.clinical_phase?.replace(/_/g, " ")} />
              <BoolRow label="Centrifugation Fit" value={enrichment.centrifugation_relevance} />
              <InfoRow label="Mfg Scale" value={enrichment.manufacturing_scale} />
              <InfoRow label="Separation Tech" value={enrichment.separation_technology_current} />
              <InfoRow label="Bioreactor" value={enrichment.bioreactor_type} />
              <InfoRow
                label="Partnerships"
                value={
                  enrichment.key_partnerships
                    ? Array.isArray(enrichment.key_partnerships)
                      ? enrichment.key_partnerships.join(", ")
                      : enrichment.key_partnerships
                    : null
                }
              />
              <InfoRow label="Drug Names" value={company.carr_drug_names} />
              <InfoRow
                label="Pipeline Count"
                value={company.carr_pipeline_count != null ? String(company.carr_pipeline_count) : null}
              />
            </div>
            {/* Competitors */}
            {enrichment.competitors?.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap mt-3">
                <span className="text-xs text-muted-foreground shrink-0">Competitors:</span>
                {enrichment.competitors.map((c: string) => (
                  <Badge key={c} variant="outline" className="text-[10px]">{c}</Badge>
                ))}
              </div>
            )}
            {/* Data sources */}
            {enrichment.data_sources?.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap mt-2">
                <span className="text-[10px] text-muted-foreground shrink-0 flex items-center gap-1">
                  <ExternalLink className="h-3 w-3" />Sources:
                </span>
                {(Array.isArray(enrichment.data_sources) ? enrichment.data_sources : [enrichment.data_sources]).map(
                  (src: string, i: number) => {
                    try {
                      const host = new URL(src).hostname.replace("www.", "");
                      return (
                        <a key={i} href={src} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-600 hover:underline">
                          {host}
                        </a>
                      );
                    } catch {
                      return (
                        <span key={i} className="text-[10px] text-muted-foreground">
                          {src}
                        </span>
                      );
                    }
                  }
                )}
              </div>
            )}
          </div>

          {/* ── TAGS ── */}
          {company.tags?.length > 0 && (
            <div className="px-5 py-3">
              <div className="flex items-center gap-2 flex-wrap">
                <Tag className="h-3 w-3 text-muted-foreground shrink-0" />
                {company.tags.map((tag: string) => (
                  <Badge key={tag} variant="outline" className="text-[10px]">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── INTELLIGENCE SIGNALS ── */}
      <div className="flex items-center gap-4 flex-wrap px-1">
        <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Signals</span>
        <div className="flex items-center gap-1.5 text-sm">
          <Users className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="font-semibold">{contacts.data?.total ?? "—"}</span>
          <span className="text-xs text-muted-foreground">contacts</span>
          {(contacts.data?.withEmail ?? 0) > 0 && (
            <span className="text-[10px] text-muted-foreground">({contacts.data?.withEmail} w/email)</span>
          )}
        </div>
        <div className="flex items-center gap-1.5 text-sm">
          <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="font-semibold">{opportunities.data?.length ?? "—"}</span>
          <span className="text-xs text-muted-foreground">pipeline</span>
        </div>
        <div className="flex items-center gap-1.5 text-sm">
          <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="font-semibold">{trials.data ?? "—"}</span>
          <span className="text-xs text-muted-foreground">trials</span>
        </div>
        <div className="flex items-center gap-1.5 text-sm">
          <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="font-semibold">{grants.data ?? "—"}</span>
          <span className="text-xs text-muted-foreground">grants</span>
        </div>
        <div className="flex items-center gap-1.5 text-sm">
          <Newspaper className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="font-semibold">{news.data ?? "—"}</span>
          <span className="text-xs text-muted-foreground">news</span>
        </div>
        <div className="flex items-center gap-1.5 text-sm">
          <Target className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="font-semibold">{campaigns.data?.length ?? "—"}</span>
          <span className="text-xs text-muted-foreground">campaigns</span>
          {(campaigns.data?.length ?? 0) > 0 && (
            <span className="text-[10px] text-muted-foreground">
              ({campaigns.data!.reduce((s, c) => s + c.contacts_enrolled, 0)} enrolled)
            </span>
          )}
        </div>
      </div>

      {/* ── ACTIVE PIPELINE OPS ── */}
      {(opportunities.data?.length ?? 0) > 0 && (
        <div>
          <h4 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <BarChart3 className="h-3.5 w-3.5" /> Active Pipeline
          </h4>
          <div className="space-y-2">
            {opportunities.data!.map((opp: any) => (
              <Card key={opp.id}>
                <CardContent className="p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <Badge style={{ backgroundColor: opp.stage_color, color: "#fff" }} className="text-xs shrink-0">
                      {opp.stage_name}
                    </Badge>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{opp.name}</p>
                      {opp.single_sales_objective && (
                        <p className="text-xs text-muted-foreground truncate">SSO: {opp.single_sales_objective}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    {opp.deal_value && <p className="text-sm font-semibold">${opp.deal_value.toLocaleString()}</p>}
                    {opp.next_action && <p className="text-xs text-muted-foreground">Next: {opp.next_action}</p>}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* ── CAMPAIGN ACTIVITY ── */}
      {(campaigns.data?.length ?? 0) > 0 && (
        <div>
          <h4 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Target className="h-3.5 w-3.5" /> Campaign Activity
          </h4>
          <div className="space-y-2">
            {campaigns.data!.map((camp: AccountCampaignSummary) => (
              <Card key={camp.campaign_id}>
                <CardContent className="p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <Badge
                      variant={camp.campaign_status === "active" ? "default" : "secondary"}
                      className="text-xs shrink-0"
                    >
                      {camp.platform === "emailbison" ? "Email" : camp.platform === "linkedin" ? "LinkedIn" : camp.platform || "—"}
                    </Badge>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{camp.campaign_name}</p>
                      <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                        {camp.sender_name && <span>by {camp.sender_name}</span>}
                        <span>{camp.contacts_enrolled} enrolled</span>
                        {camp.contacts_replied > 0 && <span className="text-green-600">{camp.contacts_replied} replied</span>}
                        {camp.meetings_booked > 0 && <span className="text-purple-600">{camp.meetings_booked} meetings</span>}
                      </div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <Badge
                      variant="outline"
                      className={`text-[10px] ${camp.campaign_status === "active" ? "bg-green-500/10 text-green-700 border-green-500/20" : ""}`}
                    >
                      {camp.campaign_status}
                    </Badge>
                    {camp.last_sent_at && (
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        Last sent{" "}
                        {new Date(camp.last_sent_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
